import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { Comment } from '@/lib/types';

export function useComments(opts: { articleId?: string; subsectionId?: string }) {
  const qs = new URLSearchParams();
  if (opts.articleId) qs.set('articleId', opts.articleId);
  if (opts.subsectionId) qs.set('subsectionId', opts.subsectionId);
  const key = ['comments', opts.articleId ?? opts.subsectionId];
  return useQuery({
    queryKey: key,
    queryFn: () => api<{ comments: Comment[] }>(`/api/comments?${qs.toString()}`),
    enabled: Boolean(opts.articleId || opts.subsectionId),
  });
}

export function useCreateComment(target: { articleId?: string; subsectionId?: string }) {
  const qc = useQueryClient();
  const key = ['comments', target.articleId ?? target.subsectionId];
  return useMutation({
    mutationFn: (data: { contenidoMd: string; parentId?: string }) =>
      api<{ comment: Comment }>('/api/comments', {
        method: 'POST',
        body: { ...target, ...data },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useUpdateComment(target: { articleId?: string; subsectionId?: string }) {
  const qc = useQueryClient();
  const key = ['comments', target.articleId ?? target.subsectionId];
  return useMutation({
    mutationFn: ({ id, contenidoMd }: { id: string; contenidoMd: string }) =>
      api<{ ok: boolean }>(`/api/comments/${id}`, { method: 'PATCH', body: { contenidoMd } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteComment(target: { articleId?: string; subsectionId?: string }) {
  const qc = useQueryClient();
  const key = ['comments', target.articleId ?? target.subsectionId];
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/comments/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}
